import { ShieldCheck, Target, Layers } from 'lucide-react'
import { Button } from '../../components/Button/Button'
import { useQuoteModal } from '../../context/QuoteContext'
import facilityImg from '../../assets/images/africa-pipetek-facility-floor.webp'
import factoryFloorImg from '../../assets/images/africa-pipetek-facility-workers-floor.webp'

function About() {
	const { openQuoteModal } = useQuoteModal()

	return (
		<main className='bg-brand-light min-h-screen'>
			{/* 1. SIMPLE HERO HEADER */}
			<section className='bg-brand-dark text-white py-12 lg:py-16 border-b border-slate-800 border-t-2 border-t-brand-red'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<h1 className='font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-4xl'>
						About <span className='text-brand-blue'>Africa Pipetek</span>
					</h1>
					<p className='text-slate-300 text-base sm:text-lg max-w-3xl mt-3 leading-relaxed'>
						Specializing in premium-quality HDPE, PVC, and fitting solutions
						engineered for African construction, agriculture, mining, and
						industrial sectors.
					</p>
				</div>
			</section>

			{/* 2. COMPANY OVERVIEW & FACILITY */}
			<section className='py-12 lg:py-16 bg-white border-b border-brand-border'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center'>
						{/* Text Column */}
						<div className='lg:col-span-7 flex flex-col items-start'>
							<h2 className='font-heading text-2xl sm:text-3xl font-bold text-brand-dark'>
								World-Class Manufacturing Facility
							</h2>

							<div className='mt-4 space-y-3 text-slate-600 text-base leading-relaxed'>
								<p>
									Built on a 20,000 m² site located in Alrode, Alberton,
									Johannesburg, South Africa, our facility is designed to
									support world-class manufacturing operations.
								</p>
								<p>
									Our factory is equipped with eight state-of-the-art production
									lines, bringing a new level of innovation and environmentally
									responsible manufacturing to the industry.
								</p>
								<p>
									Our highly skilled team possesses extensive industry
									experience, enabling us to understand our customers' unique
									requirements and deliver solutions that meet their needs.
								</p>
								<p>
									We operate our own fleet of vehicles, ensuring efficient,
									reliable, and timely delivery while consistently meeting our
									customers' expectations.
								</p>
							</div>
						</div>

						{/* Image 1 - Alrode Facility & Yard */}
						<div className='lg:col-span-5 relative'>
							<div className='relative rounded-2xl overflow-hidden shadow-md border border-brand-border bg-slate-100'>
								<img
									src={facilityImg}
									alt='Africa Pipetek Manufacturing Production Lines'
									className='w-full h-[360px] object-cover'
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* 3. OUR MISSION */}
			<section className='py-12 lg:py-16 bg-brand-light border-b border-brand-border'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='max-w-3xl mb-8'>
						<h2 className='font-heading text-2xl sm:text-3xl font-bold text-brand-dark'>
							Our Mission
						</h2>
						<p className='text-slate-600 text-base mt-2'>
							Our mission is to become the leading manufacturer of PVC and HDPE
							piping solutions in Africa. To achieve this, Africa Pipetek is
							committed to providing:
						</p>
					</div>

					<div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
						<div className='bg-white p-6 rounded-xl border border-brand-border shadow-sm'>
							<div className='w-10 h-10 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center mb-4'>
								<ShieldCheck className='w-5 h-5' />
							</div>
							<h3 className='font-heading text-base font-bold text-brand-dark mb-2'>
								Uncompromising Quality
							</h3>
							<p className='text-slate-600 text-sm leading-relaxed'>
								Uncompromising quality in manufacturing, distribution, and
								customer service.
							</p>
						</div>

						<div className='bg-white p-6 rounded-xl border border-brand-border shadow-sm'>
							<div className='w-10 h-10 rounded-lg bg-brand-red/10 text-brand-red flex items-center justify-center mb-4'>
								<Target className='w-5 h-5' />
							</div>
							<h3 className='font-heading text-base font-bold text-brand-dark mb-2'>
								Industry Standards
							</h3>
							<p className='text-slate-600 text-sm leading-relaxed'>
								Full compliance with industry standards, specifications, and
								regulatory requirements.
							</p>
						</div>

						<div className='bg-white p-6 rounded-xl border border-brand-border shadow-sm'>
							<div className='w-10 h-10 rounded-lg bg-brand-dark text-white flex items-center justify-center mb-4'>
								<Layers className='w-5 h-5' />
							</div>
							<h3 className='font-heading text-base font-bold text-brand-dark mb-2'>
								Durable & Sustainable
							</h3>
							<p className='text-slate-600 text-sm leading-relaxed'>
								Durable, reliable, and sustainable products that meet evolving
								industry needs through testing, innovation, and quality
								assurance.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* 4. FACILITY CAPABILITIES */}
			<section className='py-12 lg:py-16 bg-white'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center'>
						{/* Image 2 - Production Lines Floor */}
						<div className='lg:col-span-5 order-2 lg:order-1'>
							<div className='relative rounded-2xl overflow-hidden shadow-md border border-brand-border bg-slate-100'>
								<img
									src={factoryFloorImg}
									alt='Africa Pipetek Workers in Manufacturing Production Lines'
									className='w-full h-[360px] object-cover'
								/>
							</div>
						</div>

						{/* Text Column */}
						<div className='lg:col-span-7 order-1 lg:order-2 flex flex-col items-start'>
							<h2 className='font-heading text-2xl sm:text-3xl font-bold text-brand-dark'>
								Factory Capabilities
							</h2>

							<div className='mt-4 space-y-3 text-slate-600 text-base leading-relaxed'>
								<p>
									Our factory features eight advanced, state-of-the-art
									manufacturing production lines, incorporating the latest
									technologies to ensure precision and consistency across a wide
									range of PVC and HDPE products.
								</p>
								<p>
									With <strong>five PVC production lines</strong> manufacturing
									products ranging from <strong>16 mm to 160 mm</strong>, and{' '}
									<strong>three HDPE production lines</strong> producing sizes
									from <strong>20 mm to 315 mm</strong>, we are well-equipped to
									meet diverse industry requirements.
								</p>
								<p>
									These advanced facilities enable high production efficiency,
									consistent product quality, and rapid turnaround times,
									ensuring that we reliably meet our customers' demands.
								</p>
							</div>

							<div className='mt-6'>
								<Button variant='primary' size='md' onClick={openQuoteModal}>
									Request a Quote
								</Button>
							</div>
						</div>
					</div>
				</div>
			</section>
		</main>
	)
}

export default About
